import React, { useState } from 'react';
import adminService from '../../services/api/admin';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { useToast } from '../../components/common/Toast';
import { CheckCircle, XCircle, Clock, User, Mail, Phone, Calendar, AlertCircle } from 'lucide-react';



const TeacherApproval = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState({});

  // Fetch pending teachers
  const { data: pendingTeachers, isLoading, error } = useQuery(
    'pendingTeachers',
    () => adminService.getPendingTeachers()
  );

  // Fetch subjects and grades for approval
  const { data: subjects } = useQuery(
    'subjects',
    () => adminService.getSubjects()
  );

  const { data: grades } = useQuery(
    'grades',
    () => adminService.getGrades()
  );

  // Fetch teacher's selected subjects and grades
  const getSubjects = async (teacherId) => {
    try {
      const response = await adminService.getSubjects(teacherId);
      return response;
    } catch (error) {
      // Failed to fetch teacher subjects
      return [];
    }
  };

  // Approve teacher mutation
  const approveMutation = useMutation(
    async ({ teacherId, subjects, grades }) => {
      const response = await adminService.approveTeacher(teacherId, { subjects, grades });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pendingTeachers');
        setShowApprovalModal(false);
        setSelectedTeacher(null);
        setSelectedSubjects([]);
        setSelectedGrades([]);
      }
    }
  );

  // Reject teacher mutation
  const rejectMutation = useMutation(
    async ({ teacherId, rejectionReason }) => {
      const response = await adminService.rejectTeacher(teacherId, { rejectionReason });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pendingTeachers');
        setShowRejectionModal(false);
        setSelectedTeacher(null);
        setRejectionReason('');
      }
    }
  );

  const handleApprove = (teacher) => {
    setSelectedTeacher(teacher);
    setShowApprovalModal(true);
    
    // Fetch teacher's selected subjects and grades
    getSubjects(teacher.teacher_id).then(subjects => {
      const subjectIds = subjects.map(s => s.subject_id);
      const gradeIds = subjects.map(s => s.grade_id);
      setSelectedSubjects(subjectIds);
      setSelectedGrades(gradeIds);
      setTeacherSubjects({ ...teacherSubjects, [teacher.teacher_id]: subjects });
    });
  };

  const handleReject = (teacher) => {
    setSelectedTeacher(teacher);
    setShowRejectionModal(true);
  };

  const confirmApproval = () => {
    if (selectedSubjects.length === 0 || selectedGrades.length === 0) {
      toast.error('يرجى اختيار المواد والصفوف');
      return;
    }

    approveMutation.mutate({
      teacherId: selectedTeacher.teacher_id,
      subjects: selectedSubjects,
      grades: selectedGrades
    });
  };

  const confirmRejection = () => {
    if (!rejectionReason.trim()) {
      toast.error('يرجى إدخال سبب الرفض');
      return;
    }

    rejectMutation.mutate({
      teacherId: selectedTeacher.teacher_id,
      rejectionReason
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 ml-2" />
          <span className="text-red-800">فشل في تحميل طلبات المدرسين</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">مراجعة طلبات المدرسين</h1>
          <p className="text-gray-600 mt-1">قبول أو رفض طلبات الانضمام كمعلم</p>
        </div>

        {pendingTeachers?.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد طلبات معلقة</h3>
            <p className="text-gray-600">جميع طلبات المدرسين تمت مراجعتها</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المدرس
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    معلومات الاتصال
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المواد والصفوف المطلوبة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تاريخ التقديم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingTeachers?.map((teacher) => (
                  <tr key={teacher.teacher_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="mr-3">
                          <div className="text-sm font-medium text-gray-900">{teacher.name}</div>
                          <div className="text-sm text-gray-500">معلم</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Mail className="w-4 h-4 ml-1 text-gray-400" />
                        {teacher.email}
                      </div>
                      {teacher.phone && (
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <Phone className="w-4 h-4 ml-1 text-gray-400" />
                          {teacher.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {teacherSubjects[teacher.teacher_id] ? (
                        <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          <div className="space-y-1">
                            {teacherSubjects[teacher.teacher_id].map((item, index) => (
                              <div key={index} className="flex items-center">
                                <span className="ml-1">•</span>
                                <span>{item.subject_name} - {item.grade_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          جاري التحميل...
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Calendar className="w-4 h-4 ml-1 text-gray-400" />
                        {getArabicDate(teacher.application_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-reverse space-x-2">
                        <button
                          onClick={() => handleApprove(teacher)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircle className="w-4 h-4 ml-1" />
                          قبول
                        </button>
                        <button
                          onClick={() => handleReject(teacher)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <XCircle className="w-4 h-4 ml-1" />
                          رفض
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedTeacher && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                قبول طلب الانضمام - {selectedTeacher.name}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المواد المسموح بها
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {subjects?.map((subject) => (
                    <label key={subject.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedSubjects.includes(subject.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSubjects([...selectedSubjects, subject.id]);
                          } else {
                            setSelectedSubjects(selectedSubjects.filter(id => id !== subject.id));
                          }
                        }}
                        className="ml-2"
                      />
                      <span className="text-sm text-gray-700">{subject.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الصفوف المسموح بها
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {grades?.map((grade) => (
                    <label key={grade.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedGrades.includes(grade.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGrades([...selectedGrades, grade.id]);
                          } else {
                            setSelectedGrades(selectedGrades.filter(id => id !== grade.id));
                          }
                        }}
                        className="ml-2"
                      />
                      <span className="text-sm text-gray-700">{grade.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-reverse space-x-2">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmApproval}
                  disabled={approveMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {approveMutation.isLoading ? 'جاري المعالجة...' : 'قبول'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedTeacher && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                رفض طلب الانضمام - {selectedTeacher.name}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  سبب الرفض
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="يرجى إدخال سبب الرفض..."
                />
              </div>

              <div className="flex justify-end space-x-reverse space-x-2">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmRejection}
                  disabled={rejectMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isLoading ? 'جاري المعالجة...' : 'رفض'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherApproval;
